import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCcw, Check, AlertTriangle, Settings2, ChevronDown, ChevronRight, Info } from 'lucide-react';
import apiClient from '../../services/apiClient';

// Reusable slider component for audio settings
const AudioSlider = ({ label, value, onChange, min, max, step = 1, unit, disabled, tooltip }) => {
  return (
    <div className={`space-y-1 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1">
          <label className="text-sm text-text">{label}</label>
          {tooltip && (
            <div className="group relative">
              <Info size={14} className="text-text-muted hover:text-primary cursor-help" />
              <div className="absolute left-0 bottom-6 z-50 w-64 p-2 bg-surface border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <p className="text-xs text-text leading-relaxed">{tooltip}</p>
              </div>
            </div>
          )}
        </div>
        <span className="text-sm font-mono text-text-muted">
          {typeof value === 'number' ? value.toFixed(step < 1 ? 1 : 0) : value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary disabled:cursor-not-allowed"
      />
      <div className="flex justify-between text-xs text-text-muted">
        <span>{min} {unit}</span>
        <span>{max} {unit}</span>
      </div>
    </div>
  );
};

// Toggle switch component
const ToggleSwitch = ({ enabled, onChange, disabled }) => {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-primary' : 'bg-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-0.5'
      }`} />
    </button>
  );
};

// Collapsible section component
const CollapsibleSection = ({ title, description, expanded, onToggle, children, icon: Icon }) => {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 bg-surface hover:bg-background transition-colors cursor-pointer"
      >
        {Icon && <Icon size={20} className="text-primary" />}
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-text">{title}</p>
          <p className="text-xs text-text-muted">{description}</p>
        </div>
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      {expanded && (
        <div className="p-4 bg-surface border-t border-border space-y-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Default enrichment prompt (same as backend)
const DEFAULT_ENRICHMENT_PROMPT = `Bitte formatiere das folgende Rohtranskript einer Fahrzeuginspektion in saubere, gut strukturierte Bulletpoints.
Korrigiere Rechtschreib- und Grammatikfehler, aber behalte alle technischen Informationen bei. Ergänze keine neuen Informationen.
Füge keine eigenen Bewertungen hinzu. Output soll nur inhalte des Transkripts wiedergeben.

Verwende dieses Format:
• Jeder wichtige Punkt als Bulletpoint
• Klare und professionelle Sprache
• Gruppiere ähnliche Themen zusammen
• Behalte alle Fahrzeugteile, Messungen und Befunde bei

ROHTRANSKRIPT:
{transcript}

Antworte NUR mit den formatierten Bulletpoints, ohne zusätzliche Erklärungen.`;

// Default audio config (same as backend)
const DEFAULT_AUDIO_CONFIG = {
  high_pass_filter: { enabled: true, cutoff_hz: 80 },
  noise_reduction: { enabled: true, noise_floor_db: -25, noise_reduction_strength: 25 },
  silence_removal: { enabled: true, threshold_db: -35, min_silence_duration_sec: 1.5, keep_buffer_sec: 0.3 },
  normalization: { enabled: true, target_loudness_lufs: -16, true_peak_db: -1.5 },
  output: { sample_rate: 16000, channels: 1 }
};

const SystemSettingsTab = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');

  // Settings state
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [featureFlags, setFeatureFlags] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  // Audio config state
  const [audioConfig, setAudioConfig] = useState(DEFAULT_AUDIO_CONFIG);
  const [audioConfigUpdatedAt, setAudioConfigUpdatedAt] = useState(null);
  const [audioSaveStatus, setAudioSaveStatus] = useState('idle');
  const [expandedSections, setExpandedSections] = useState({
    high_pass_filter: false,
    noise_reduction: false,
    silence_removal: true, // Start with silence removal expanded
    normalization: false,
    output: false
  });

  const debounceTimerRef = useRef(null);
  const audioDebounceTimerRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both system settings and audio config in parallel
      const [settingsResponse, audioResponse] = await Promise.all([
        apiClient.get('/admin/settings'),
        apiClient.get('/admin/audio-config').catch(() => ({ data: DEFAULT_AUDIO_CONFIG }))
      ]);

      const settings = settingsResponse.data;
      setDefaultPrompt(settings.defaultEnrichmentPrompt || DEFAULT_ENRICHMENT_PROMPT);
      setOriginalPrompt(settings.defaultEnrichmentPrompt || DEFAULT_ENRICHMENT_PROMPT);
      setFeatureFlags(settings.featureFlags || {});
      setUpdatedAt(settings.updatedAt);
      setUpdatedBy(settings.updatedBy);

      // Audio config
      const audio = audioResponse.data;
      setAudioConfig({
        high_pass_filter: audio.high_pass_filter || DEFAULT_AUDIO_CONFIG.high_pass_filter,
        noise_reduction: audio.noise_reduction || DEFAULT_AUDIO_CONFIG.noise_reduction,
        silence_removal: audio.silence_removal || DEFAULT_AUDIO_CONFIG.silence_removal,
        normalization: audio.normalization || DEFAULT_AUDIO_CONFIG.normalization,
        output: audio.output || DEFAULT_AUDIO_CONFIG.output
      });
      setAudioConfigUpdatedAt(audio.updatedAt);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(t('admin.system_settings.load_error') || 'Failed to load settings');
      setDefaultPrompt(DEFAULT_ENRICHMENT_PROMPT);
      setOriginalPrompt(DEFAULT_ENRICHMENT_PROMPT);
    } finally {
      setLoading(false);
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    }
  };

  const performSave = useCallback(async (promptToSave, flagsToSave) => {
    if (promptToSave && !promptToSave.includes('{transcript}')) {
      setError(t('settings.placeholder_hint'));
      setSaveStatus('error');
      return;
    }

    if (promptToSave && promptToSave.length > 4000) {
      setError(t('settings.prompt_too_long') || 'Prompt is too long');
      setSaveStatus('error');
      return;
    }

    try {
      setSaveStatus('saving');
      setError(null);

      const response = await apiClient.put('/admin/settings', {
        defaultEnrichmentPrompt: promptToSave,
        featureFlags: flagsToSave
      });

      setOriginalPrompt(promptToSave);
      setUpdatedAt(response.data.updatedAt);
      setUpdatedBy(response.data.updatedBy);
      setSaveStatus('saved');

      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving system settings:', err);
      setError(err.response?.data?.detail || t('admin.system_settings.save_error'));
      setSaveStatus('error');
    }
  }, [t]);

  // Debounced auto-save for prompt
  useEffect(() => {
    if (isInitialLoadRef.current || loading) return;
    if (defaultPrompt === originalPrompt) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSave(defaultPrompt, featureFlags);
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [defaultPrompt, originalPrompt, loading, performSave, featureFlags]);

  const handleReset = () => {
    setDefaultPrompt(DEFAULT_ENRICHMENT_PROMPT);
    setError(null);
    setSaveStatus('idle');
  };

  const handleFeatureFlagToggle = (flag) => {
    const newFlags = { ...featureFlags, [flag]: !featureFlags[flag] };
    setFeatureFlags(newFlags);
    performSave(defaultPrompt, newFlags);
  };

  // Audio config save function
  const performAudioSave = useCallback(async (configToSave) => {
    try {
      setAudioSaveStatus('saving');

      const response = await apiClient.put('/admin/audio-config', configToSave);

      setAudioConfigUpdatedAt(response.data.updatedAt);
      setAudioSaveStatus('saved');

      setTimeout(() => setAudioSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving audio config:', err);
      setError(err.response?.data?.detail || t('admin.audio_config.save_error'));
      setAudioSaveStatus('error');
    }
  }, [t]);

  // Handle audio config changes with debounce
  const handleAudioConfigChange = (section, field, value) => {
    const newConfig = {
      ...audioConfig,
      [section]: {
        ...audioConfig[section],
        [field]: value
      }
    };
    setAudioConfig(newConfig);

    // Debounced save
    if (audioDebounceTimerRef.current) {
      clearTimeout(audioDebounceTimerRef.current);
    }
    audioDebounceTimerRef.current = setTimeout(() => {
      performAudioSave(newConfig);
    }, 1000);
  };

  // Handle section toggle (enable/disable entire processing stage)
  const handleSectionToggle = (section) => {
    handleAudioConfigChange(section, 'enabled', !audioConfig[section].enabled);
  };

  // Toggle expanded state for a section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Reset audio config to defaults
  const handleAudioReset = async () => {
    try {
      setAudioSaveStatus('saving');
      const response = await apiClient.post('/admin/audio-config/reset');
      setAudioConfig({
        high_pass_filter: response.data.high_pass_filter,
        noise_reduction: response.data.noise_reduction,
        silence_removal: response.data.silence_removal,
        normalization: response.data.normalization,
        output: response.data.output
      });
      setAudioConfigUpdatedAt(response.data.updatedAt);
      setAudioSaveStatus('saved');
      setTimeout(() => setAudioSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error resetting audio config:', err);
      setError(err.response?.data?.detail || t('admin.audio_config.reset_error'));
      setAudioSaveStatus('error');
    }
  };

  const renderAudioSaveStatus = () => {
    switch (audioSaveStatus) {
      case 'saving':
        return (
          <span className="flex items-center gap-1 text-text-muted">
            <Loader2 size={14} className="animate-spin" />
            {t('settings.saving')}
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1 text-success">
            <Check size={14} />
            {t('settings.saved')}
          </span>
        );
      case 'error':
        return (
          <span className="text-error">
            {t('common.error')}
          </span>
        );
      default:
        return null;
    }
  };

  const renderSaveStatus = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <span className="flex items-center gap-1 text-text-muted">
            <Loader2 size={14} className="animate-spin" />
            {t('settings.saving')}
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1 text-success">
            <Check size={14} />
            {t('settings.saved')}
          </span>
        );
      case 'error':
        return (
          <span className="text-error">
            {t('common.error')}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* System Settings Header */}
      <div>
        <h2 className="text-xl font-bold text-text mb-1">
          {t('admin.system_settings.title')}
        </h2>
        <p className="text-sm text-text-muted">
          {t('admin.system_settings.description')}
        </p>
        {updatedAt && (
          <p className="text-xs text-text-muted mt-2">
            {t('admin.system_settings.last_updated')}: {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle size={18} />
          {error}
        </div>
      )}

      {/* Default Enrichment Prompt Card */}
      <div className="card-elevated p-4">
        <h3 className="text-lg font-semibold text-secondary mb-2">
          {t('admin.system_settings.default_prompt')}
        </h3>
        <p className="text-sm text-text-muted mb-4">
          {t('admin.system_settings.default_prompt_desc')}
        </p>

        <textarea
          value={defaultPrompt}
          onChange={(e) => setDefaultPrompt(e.target.value)}
          rows={12}
          className="input font-mono text-sm leading-relaxed resize-y mb-2"
        />

        {/* Character Count and Save Status */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-xs">
            {renderSaveStatus()}
          </div>
          <p className={`text-xs ${defaultPrompt.length > 4000 ? 'text-error' : 'text-text-muted'}`}>
            {defaultPrompt.length} / 4000
          </p>
        </div>

        {/* Reset Button */}
        <button
          onClick={handleReset}
          disabled={saveStatus === 'saving'}
          className="btn-secondary flex items-center gap-2"
        >
          <RotateCcw size={18} />
          {t('settings.reset')}
        </button>
      </div>

      {/* Feature Flags Card */}
      <div className="card-elevated p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-secondary">
            {t('admin.system_settings.feature_flags')}
          </h3>
          <div className="text-xs">
            {renderSaveStatus()}
          </div>
        </div>
        <p className="text-sm text-text-muted mb-4">
          {t('admin.system_settings.feature_flags_desc')}
        </p>

        <div className="space-y-3">
          {/* Danger Zone for All Users Toggle */}
          <button
            onClick={() => handleFeatureFlagToggle('dangerZoneEnabledForAll')}
            className="w-full flex items-center gap-3 py-3 cursor-pointer text-left hover:bg-background px-2 rounded-lg transition-colors"
          >
            <AlertTriangle
              size={24}
              className={featureFlags.dangerZoneEnabledForAll ? 'text-warning' : 'text-text-muted'}
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text">
                {t('admin.system_settings.danger_zone_all_users')}
              </p>
              <p className="text-xs text-text-muted">
                {t('admin.system_settings.danger_zone_all_users_desc')}
              </p>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${
              featureFlags.dangerZoneEnabledForAll ? 'bg-warning' : 'bg-border'
            }`}>
              <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${
                featureFlags.dangerZoneEnabledForAll ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </div>
          </button>

        </div>
      </div>

      {/* Audio Processing Pipeline Card */}
      <div className="card-elevated p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-secondary">
              {t('admin.audio_config.title')}
            </h3>
            <div className="group relative">
              <Info size={18} className="text-text-muted hover:text-primary cursor-help" />
              <div className="absolute left-0 top-6 z-50 w-80 p-3 bg-surface border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <p className="text-xs text-text leading-relaxed">
                  {t('admin.audio_config.tooltip')}
                </p>
              </div>
            </div>
          </div>
          <div className="text-xs">
            {renderAudioSaveStatus()}
          </div>
        </div>
        <p className="text-sm text-text-muted mb-4">
          {t('admin.audio_config.description')}
        </p>
        {audioConfigUpdatedAt && (
          <p className="text-xs text-text-muted mb-4">
            {t('admin.system_settings.last_updated')}: {new Date(audioConfigUpdatedAt).toLocaleString()}
          </p>
        )}

        <div className="space-y-3">
          {/* Silence Removal - Primary feature, shown first */}
          <CollapsibleSection
            title={t('admin.audio_config.silence_removal.title')}
            description={t('admin.audio_config.silence_removal.description')}
            expanded={expandedSections.silence_removal}
            onToggle={() => toggleSection('silence_removal')}
            icon={Settings2}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text">{t('admin.audio_config.enabled')}</span>
              <ToggleSwitch
                enabled={audioConfig.silence_removal.enabled}
                onChange={() => handleSectionToggle('silence_removal')}
              />
            </div>
            <AudioSlider
              label={t('admin.audio_config.silence_removal.threshold_db')}
              value={audioConfig.silence_removal.threshold_db}
              onChange={(v) => handleAudioConfigChange('silence_removal', 'threshold_db', v)}
              min={-60}
              max={0}
              step={1}
              unit="dB"
              disabled={!audioConfig.silence_removal.enabled}
              tooltip={t('admin.audio_config.silence_removal.threshold_db_tooltip')}
            />
            <AudioSlider
              label={t('admin.audio_config.silence_removal.min_duration')}
              value={audioConfig.silence_removal.min_silence_duration_sec}
              onChange={(v) => handleAudioConfigChange('silence_removal', 'min_silence_duration_sec', v)}
              min={0.5}
              max={10}
              step={0.5}
              unit="s"
              disabled={!audioConfig.silence_removal.enabled}
              tooltip={t('admin.audio_config.silence_removal.min_duration_tooltip')}
            />
            <AudioSlider
              label={t('admin.audio_config.silence_removal.keep_buffer')}
              value={audioConfig.silence_removal.keep_buffer_sec}
              onChange={(v) => handleAudioConfigChange('silence_removal', 'keep_buffer_sec', v)}
              min={0}
              max={1}
              step={0.1}
              unit="s"
              disabled={!audioConfig.silence_removal.enabled}
              tooltip={t('admin.audio_config.silence_removal.keep_buffer_tooltip')}
            />
          </CollapsibleSection>

          {/* High-Pass Filter */}
          <CollapsibleSection
            title={t('admin.audio_config.high_pass_filter.title')}
            description={t('admin.audio_config.high_pass_filter.description')}
            expanded={expandedSections.high_pass_filter}
            onToggle={() => toggleSection('high_pass_filter')}
            icon={Settings2}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text">{t('admin.audio_config.enabled')}</span>
              <ToggleSwitch
                enabled={audioConfig.high_pass_filter.enabled}
                onChange={() => handleSectionToggle('high_pass_filter')}
              />
            </div>
            <AudioSlider
              label={t('admin.audio_config.high_pass_filter.cutoff_hz')}
              value={audioConfig.high_pass_filter.cutoff_hz}
              onChange={(v) => handleAudioConfigChange('high_pass_filter', 'cutoff_hz', v)}
              min={20}
              max={500}
              step={10}
              unit="Hz"
              disabled={!audioConfig.high_pass_filter.enabled}
              tooltip={t('admin.audio_config.high_pass_filter.cutoff_hz_tooltip')}
            />
          </CollapsibleSection>

          {/* Noise Reduction */}
          <CollapsibleSection
            title={t('admin.audio_config.noise_reduction.title')}
            description={t('admin.audio_config.noise_reduction.description')}
            expanded={expandedSections.noise_reduction}
            onToggle={() => toggleSection('noise_reduction')}
            icon={Settings2}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text">{t('admin.audio_config.enabled')}</span>
              <ToggleSwitch
                enabled={audioConfig.noise_reduction.enabled}
                onChange={() => handleSectionToggle('noise_reduction')}
              />
            </div>
            <AudioSlider
              label={t('admin.audio_config.noise_reduction.noise_floor_db')}
              value={audioConfig.noise_reduction.noise_floor_db}
              onChange={(v) => handleAudioConfigChange('noise_reduction', 'noise_floor_db', v)}
              min={-60}
              max={0}
              step={1}
              unit="dB"
              disabled={!audioConfig.noise_reduction.enabled}
              tooltip={t('admin.audio_config.noise_reduction.noise_floor_db_tooltip')}
            />
            <AudioSlider
              label={t('admin.audio_config.noise_reduction.strength')}
              value={audioConfig.noise_reduction.noise_reduction_strength}
              onChange={(v) => handleAudioConfigChange('noise_reduction', 'noise_reduction_strength', v)}
              min={0}
              max={100}
              step={5}
              unit="%"
              disabled={!audioConfig.noise_reduction.enabled}
              tooltip={t('admin.audio_config.noise_reduction.strength_tooltip')}
            />
          </CollapsibleSection>

          {/* Normalization */}
          <CollapsibleSection
            title={t('admin.audio_config.normalization.title')}
            description={t('admin.audio_config.normalization.description')}
            expanded={expandedSections.normalization}
            onToggle={() => toggleSection('normalization')}
            icon={Settings2}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text">{t('admin.audio_config.enabled')}</span>
              <ToggleSwitch
                enabled={audioConfig.normalization.enabled}
                onChange={() => handleSectionToggle('normalization')}
              />
            </div>
            <AudioSlider
              label={t('admin.audio_config.normalization.target_loudness')}
              value={audioConfig.normalization.target_loudness_lufs}
              onChange={(v) => handleAudioConfigChange('normalization', 'target_loudness_lufs', v)}
              min={-30}
              max={-5}
              step={1}
              unit="LUFS"
              disabled={!audioConfig.normalization.enabled}
              tooltip={t('admin.audio_config.normalization.target_loudness_tooltip')}
            />
            <AudioSlider
              label={t('admin.audio_config.normalization.true_peak')}
              value={audioConfig.normalization.true_peak_db}
              onChange={(v) => handleAudioConfigChange('normalization', 'true_peak_db', v)}
              min={-10}
              max={0}
              step={0.5}
              unit="dB"
              disabled={!audioConfig.normalization.enabled}
              tooltip={t('admin.audio_config.normalization.true_peak_tooltip')}
            />
          </CollapsibleSection>

          {/* Output Settings */}
          <CollapsibleSection
            title={t('admin.audio_config.output.title')}
            description={t('admin.audio_config.output.description')}
            expanded={expandedSections.output}
            onToggle={() => toggleSection('output')}
            icon={Settings2}
          >
            <AudioSlider
              label={t('admin.audio_config.output.sample_rate')}
              value={audioConfig.output.sample_rate}
              onChange={(v) => handleAudioConfigChange('output', 'sample_rate', v)}
              min={8000}
              max={48000}
              step={8000}
              unit="Hz"
              tooltip={t('admin.audio_config.output.sample_rate_tooltip')}
            />
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-1">
                <span className="text-sm text-text">{t('admin.audio_config.output.channels')}</span>
                <div className="group relative">
                  <Info size={14} className="text-text-muted hover:text-primary cursor-help" />
                  <div className="absolute left-0 bottom-6 z-50 w-64 p-2 bg-surface border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <p className="text-xs text-text leading-relaxed">{t('admin.audio_config.output.channels_tooltip')}</p>
                  </div>
                </div>
              </div>
              <select
                value={audioConfig.output.channels}
                onChange={(e) => handleAudioConfigChange('output', 'channels', parseInt(e.target.value))}
                className="input w-24 text-sm"
              >
                <option value={1}>{t('admin.audio_config.output.mono')}</option>
                <option value={2}>{t('admin.audio_config.output.stereo')}</option>
              </select>
            </div>
          </CollapsibleSection>
        </div>

        {/* Reset Audio Config Button */}
        <button
          onClick={handleAudioReset}
          disabled={audioSaveStatus === 'saving'}
          className="btn-secondary flex items-center gap-2 mt-4"
        >
          <RotateCcw size={18} />
          {t('admin.audio_config.reset')}
        </button>
      </div>
    </div>
  );
};

export default SystemSettingsTab;
